import {
  ChangeDetectionStrategy,
  Component,
  computed,
  CUSTOM_ELEMENTS_SCHEMA,
  type ElementRef,
  inject,
  input,
  type OnDestroy,
  resource,
  signal,
  viewChild,
} from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import {
  PronunciationSpeechService,
  SLOW_PLAYBACK_RATE,
} from '../../../services/pronunciation-speech/pronunciation-speech';
import { GuidedTourAnchorDirective } from '../../guided-tour/guided-tour-anchor';

const SENTENCE_PLAYBACK_ID = 'sentence';
const SLOW_SENTENCE_PLAYBACK_ID = 'sentence-slow';
const SPEECH_ITEM_SELECTOR = '[data-speech-item]';
const SENTENCE_ITEM_INDEX = 0;
const SLOW_ITEM_INDEX = 1;

let nextInstanceId = 0;

/**
 * The play button and the turtle that read the sentence of a voice question aloud before the student
 * records it, so they can practise the pronunciation first. Nothing shows while the sentence
 * cannot be synthesised.
 */
@Component({
  selector: 'nr-sentence-speech',
  imports: [TranslatePipe, GuidedTourAnchorDirective],
  templateUrl: './sentence-speech.html',
  styleUrl: './sentence-speech.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '(keydown)': 'onKeydown($event)',
  },
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class SentenceSpeechComponent implements OnDestroy {
  private readonly pronunciationSpeech = inject(PronunciationSpeechService);
  private readonly toolbar = viewChild<ElementRef<HTMLElement>>('toolbar');

  private readonly speechAvailability = resource({
    params: computed(() => {
      const text = this.sentenceText();
      if (!this.pronunciationSpeech.isSupported() || !text) return undefined;
      return { text, questionId: this.questionId() };
    }),
    loader: async ({ params: { text, questionId } }) =>
      this.pronunciationSpeech.load(text, questionId).then(
        () => true,
        () => false,
      ),
  });

  readonly text = input.required<string>();

  readonly questionId = input.required<string>();

  readonly disabled = input(false);

  readonly listenId = `sentence-speech-listen-${nextInstanceId}`;

  readonly slowId = `sentence-speech-slow-${nextInstanceId++}`;

  readonly sentenceItemIndex = SENTENCE_ITEM_INDEX;

  readonly slowItemIndex = SLOW_ITEM_INDEX;

  readonly activeItemIndex = signal(SENTENCE_ITEM_INDEX);

  readonly sentenceText = computed(() => this.text().trim().replace(/\s+/g, ' '));

  readonly speechAvailable = computed(() => this.speechAvailability.value() === true);

  readonly activePlayback = computed(() => {
    const playback = this.pronunciationSpeech.playback();
    return playback?.owner === this ? playback : undefined;
  });

  readonly isSentencePlaying = computed(() => this.activePlayback()?.id === SENTENCE_PLAYBACK_ID);

  readonly isSlowSentencePlaying = computed(
    () => this.activePlayback()?.id === SLOW_SENTENCE_PLAYBACK_ID,
  );

  ngOnDestroy(): void {
    this.pronunciationSpeech.stop(this);
  }

  toggleSentence(): void {
    this.togglePlayback(SENTENCE_PLAYBACK_ID, this.isSentencePlaying());
  }

  toggleSlowSentence(): void {
    this.togglePlayback(
      SLOW_SENTENCE_PLAYBACK_ID,
      this.isSlowSentencePlaying(),
      SLOW_PLAYBACK_RATE,
    );
  }

  /** The play button and the turtle are a single tab stop: arrows, Home and End move between them. */
  onKeydown(event: KeyboardEvent): void {
    const toolbar = this.toolbar()?.nativeElement;
    if (!toolbar || !(event.target instanceof Node) || !toolbar.contains(event.target)) return;
    const targetIndex = SentenceSpeechComponent.getTargetItemIndex(event.key);
    if (targetIndex === undefined) return;

    event.preventDefault();
    this.activeItemIndex.set(targetIndex);
    Array.from(toolbar.querySelectorAll<HTMLElement>(SPEECH_ITEM_SELECTOR))
      .at(targetIndex)
      ?.focus();
  }

  private togglePlayback(playbackId: string, isPlaying: boolean, rate?: number): void {
    if (isPlaying) {
      this.pronunciationSpeech.stop(this);
      return;
    }
    if (this.disabled()) return;
    this.pronunciationSpeech.play({
      text: this.sentenceText(),
      questionId: this.questionId(),
      owner: this,
      id: playbackId,
      ...(rate && { rate }),
    });
  }

  private static getTargetItemIndex(key: string): number | undefined {
    if (key === 'ArrowRight' || key === 'End') return SLOW_ITEM_INDEX;
    if (key === 'ArrowLeft' || key === 'Home') return SENTENCE_ITEM_INDEX;
    return undefined;
  }
}
